<script setup>
//SelectedFilters.vue
import { ref, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import TableFilter from './TableFilter.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useOperators } from 'dashboard/components-next/filter/operators.js';
import { Edit3, Plus, Trash2 } from 'lucide-vue-next';

const { t } = useI18n();
const { operators } = useOperators();

const props = defineProps({
  filterTypes: { type: Array, required: true, default: () => [] },
  selectedTable: { type: String, default: '' },
  initialFilters: { type: Array, default: () => [] },
});

const emit = defineEmits(['apply-filter', 'clear-filters', 'preview-table', 'change']);

const dialogRef = ref(null);
const showAppliedFilters = ref(false);
const filters = ref([]);
const isDialogClosing = ref(false);
const isInitialMount = ref(true);
const hasInitializedTable = ref(false);
const hasUserInteraction = ref(false);
const filtersLocked = ref(false);

const initializeFilters = () => {
  if (props.initialFilters.length > 0 && props.filterTypes.length > 0) {
    const validFilters = props.initialFilters.filter(filter =>
      props.filterTypes.some(type => type.attributeKey === filter.attributeKey)
    );

    filters.value = [...validFilters];
    showAppliedFilters.value = validFilters.length > 0;

    if (validFilters.length > 0) {
      hasUserInteraction.value = true;
      filtersLocked.value = true; 
    }
  } else if (!filtersLocked.value) {
    filters.value = [];
    showAppliedFilters.value = false;
  }
};

nextTick(() => {
  initializeFilters();
});

const rules = {
  filters: {
    required,
    $each: {
      attributeKey: { required },
      filterOperator: { required },
      values: {
        required,
        validValues: (value) =>
          Array.isArray(value)
            ? value.length > 0 && value.every((val) => val !== '' && val !== null)
            : value !== '' && value !== null,
      },
    },
  },
};

const v$ = useVuelidate(rules, { filters });

const formErrors = computed(() => ({
  filters: v$.value.filters.$error
    ? v$.value.filters.$errors.some((error) => error.$validator === 'required')
      ? t('WHATSAPP_TEMPLATES.FILTERS.ERROR_NO_FILTERS')
      : t('WHATSAPP_TEMPLATES.FILTERS.ERROR_INVALID_FILTERS')
    : '',
}));

const validateAndEmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return false;
  }
  emit('apply-filter', filters.value);
  return true;
};

const formatFilterDisplay = (filter) => {
  const attributeKey = filter.attributeKey || 'Atributo não definido';
  const operatorObj = operators.value[filter.filterOperator];
  const operatorDisplay = operatorObj ? operatorObj.label : filter.filterOperator || '=';
  const values = Array.isArray(filter.values)
    ? filter.values.join(', ')
    : filter.values ?? '';

  if (filter.filterOperator === 'due_in_days') {
    return `${attributeKey} vence em ${values} dias`;
  }

  if (!values.trim()) {
    return `${attributeKey} ${operatorDisplay} [Nenhum valor]`;
  }

  return `${attributeKey} ${operatorDisplay} ${values}`;
};

const removeFilter = (index) => {
  hasUserInteraction.value = true;
  filtersLocked.value = true;

  const updatedFilters = filters.value.filter((_, i) => i !== index);
  filters.value = updatedFilters;
  showAppliedFilters.value = updatedFilters.length > 0;
  emit('apply-filter', updatedFilters);
  v$.value.$reset();
};

const openFilterDialog = () => {
  hasUserInteraction.value = true;
  filtersLocked.value = true;

  if (dialogRef.value) {
    if (!props.filterTypes || props.filterTypes.length === 0) {
      console.warn('filterTypes não está carregado. Aguardando dados...');
      return;
    }

    dialogRef.value.open();

    if (filters.value.length === 0 && props.filterTypes.length > 0 && !props.initialFilters.length) {
      const firstFilterType = props.filterTypes[0].attributeKey || 'concate';
      filters.value = [
        {
          id: Date.now() + Math.random(),
          attributeKey: firstFilterType,
          filterOperator: 'equal_to',
          values: '',
          queryOperator: 'and',
          attributeModel: 'standard',
        },
      ];
      emit('apply-filter', filters.value);
    }

    showAppliedFilters.value = filters.value.length > 0;
    v$.value.$reset();
  }
};

const closeFilterDialog = () => {
  if (isDialogClosing.value) return;

  isDialogClosing.value = true;
  showAppliedFilters.value = filters.value.length > 0;
  emit('apply-filter', filters.value);
  v$.value.$reset();

  setTimeout(() => {
    isDialogClosing.value = false;
  }, 100);
};

const applyFilter = (newFilters) => {
  hasUserInteraction.value = true;
  filtersLocked.value = true;

  filters.value = newFilters;
  showAppliedFilters.value = newFilters.length > 0;
  emit('apply-filter', newFilters);
  v$.value.$reset();
};

const clearFilters = () => {
  hasUserInteraction.value = true;
  filtersLocked.value = false;

  filters.value = [];
  showAppliedFilters.value = false;
  emit('clear-filters');
  v$.value.$reset();
};

const updateFilters = (newFilters) => {
  filters.value = [...newFilters];
  showAppliedFilters.value = newFilters.length > 0;
  hasUserInteraction.value = true;
  filtersLocked.value = true;
};

const forceUpdate = () => {
  showAppliedFilters.value = filters.value.length > 0;
  hasUserInteraction.value = true;
};

defineExpose({
  resetFilters: () => {
    filtersLocked.value = false;
    filters.value = [];
    showAppliedFilters.value = false;
    emit('clear-filters');
    v$.value.$reset();
  },
  validateAndEmit,
  filters,
  updateFilters,
  forceUpdate,
});

watch(
  () => props.initialFilters,
  (newInitialFilters, oldInitialFilters) => {
    if (filtersLocked.value && filters.value.length > 0) {
      return;
    }

    if (newInitialFilters && newInitialFilters.length > 0 && props.filterTypes.length > 0) {
      const validFilters = newInitialFilters.filter(filter =>
        props.filterTypes.some(type => type.attributeKey === filter.attributeKey)
      );

      filters.value = [...validFilters];
      showAppliedFilters.value = validFilters.length > 0;
      v$.value.$reset();

      if (validFilters.length > 0) {
        hasUserInteraction.value = true;
        filtersLocked.value = true; 
      }
    }
  },
  { deep: true, immediate: true }
);

watch(
  filters,
  (newFilters) => {
    showAppliedFilters.value = newFilters.length > 0;
  },
  { immediate: true, deep: true }
);

watch(
  () => props.filterTypes,
  (newFilterTypes) => {
    if (!filtersLocked.value && newFilterTypes && newFilterTypes.length > 0 && props.initialFilters.length > 0) {
      const validFilters = props.initialFilters.filter(filter =>
        newFilterTypes.some(type => type.attributeKey === filter.attributeKey)
      );

      filters.value = [...validFilters];
      showAppliedFilters.value = validFilters.length > 0;
      v$.value.$reset();

      if (validFilters.length > 0) {
        hasUserInteraction.value = true;
        filtersLocked.value = true;
      }
    }
  }
);

watch(
  () => props.selectedTable,
  (newTable, oldTable) => {
    if (!hasInitializedTable.value && newTable) {
      hasInitializedTable.value = true;
      return;
    }

    if (isInitialMount.value && props.initialFilters.length > 0) {
      isInitialMount.value = false;
      return;
    }

    if (newTable !== oldTable && hasInitializedTable.value && oldTable && newTable && !filtersLocked.value) {
      hasUserInteraction.value = true;
      filters.value = [];
      showAppliedFilters.value = false;
      emit('clear-filters');
      v$.value.$reset();
    }
  },
  { immediate: true }
);

watch(
  filters,
  (newFilters, oldFilters) => {
    showAppliedFilters.value = newFilters.length > 0;

    if (!hasUserInteraction.value) {
      return;
    }

    emit('change', [...newFilters]);
  },
  { deep: true }
);
</script>

<template>
  <div class="space-y-6">
    <!-- Applied Filters Display -->
    <div class="flex flex-col gap-3">
      <div class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
        <h3 class="text-sm font-medium text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
          <span>🔍</span>
          {{ t('WHATSAPP_TEMPLATES.FILTERS.APPLIED_FILTERS') }}
        </h3>

        <div v-if="showAppliedFilters && filters.length > 0" class="flex flex-wrap gap-2">
          <div
            v-for="(filter, index) in filters"
            :key="index"
            class="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-800 text-green-700 dark:text-green-300 text-sm rounded-full border border-green-300 dark:border-green-700 shadow-sm"
          >
            <span>{{ formatFilterDisplay(filter) }}</span>
            <button
              type="button"
              class="text-green-600 dark:text-green-400 hover:text-red-600 dark:hover:text-red-400 focus:outline-none transition-colors ml-1"
              @click="removeFilter(index)"
              aria-label="Remover filtro"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div v-else class="text-sm text-green-600 dark:text-green-400 bg-white dark:bg-slate-800 rounded-lg p-3 border border-green-200 dark:border-green-700">
          <div class="flex items-center gap-2">
            <span>ℹ️</span>
            <span>{{ t('WHATSAPP_TEMPLATES.FILTERS.NO_FILTERS') }} - Todos os registros da tabela serão incluídos na campanha.</span>
          </div>
        </div>
      </div>

      <p v-if="formErrors?.filters" class="text-xs text-red-600 dark:text-red-400">
        {{ formErrors.filters }}
      </p>
    </div>

    <!-- Filter Actions -->
    <div class="flex gap-3">
      <!-- Primary Button (Add/Edit Filters) -->
      <button
        type="button"
        @click="openFilterDialog"
        class="modern-button primary-button"
      >
        <div class="primary-gradient"></div>
        <div class="primary-glow"></div>
        <div class="button-content">
          <span class="button-text">
            <Edit3 v-if="filters.length > 0" class="button-icon" />
            <Plus v-else class="button-icon" />
            {{ filters.length > 0 ? 'Editar Filtros' : 'Adicionar Filtros' }}
          </span>
        </div>
        <div class="button-shine"></div>
      </button>

      <!-- Clear Button -->
      <button
        v-if="filters.length > 0"
        type="button"
        @click="clearFilters"
        class="modern-button clear-button"
      >
        <div class="clear-gradient"></div>
        <div class="clear-glow"></div>
        <div class="button-content">
          <span class="button-text">
            <Trash2 class="button-icon" />
            Limpar Filtros
          </span>
        </div>
        <div class="button-shine"></div>
      </button>
    </div>

    <!-- Filter Dialog -->
    <Dialog
      ref="dialogRef"
      :title="t('WHATSAPP_TEMPLATES.FILTERS.DIALOG_TITLE')"
      width="5xl"
      :show-cancel-button="true"
      :show-confirm-button="false"
      cancel-button-label="Fechar"
      @close="closeFilterDialog"
    >
      <template #default>
        <div class="p-4">
          <TableFilter
            v-model="filters"
            :filter-types="props.filterTypes"
            class="mt-1"
            @apply-filter="applyFilter"
            @clear-filters="clearFilters"
            @change="onTableFilterChange"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
/* Base Button Styles - Copiado do TabContentWrapper */
.modern-button {
  position: relative;
  height: 48px;
  padding: 0 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 140px;
  overflow: hidden;
}

.modern-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Button Shine Effect */
.button-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
  transform: translateX(-100%);
  transition: transform 0.6s;
  pointer-events: none;
}

.modern-button:hover .button-shine {
  transform: translateX(100%);
}

/* Primary Button (Add/Edit Filters) */
.primary-button {
  position: relative;
  color: white;
  font-weight: 700;
  border: none;
}

.primary-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgb(var(--teal-9)), rgb(var(--teal-10)), rgb(var(--teal-11)));
  border-radius: 12px;
  transition: all 0.3s;
}

.primary-glow {
  position: absolute;
  inset: -4px;
  background: linear-gradient(135deg, rgb(var(--teal-6) / 0.6), rgb(var(--teal-8) / 0.4));
  border-radius: 16px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
  filter: blur(12px);
}

.button-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.primary-button:hover {
  transform: translateY(-2px) scale(1.05);
}

.primary-button:hover .primary-gradient {
  background: linear-gradient(135deg, rgb(var(--teal-10)), rgb(var(--teal-11)), rgb(var(--teal-12)));
  animation: gradient-shift 2s ease-in-out infinite;
}

.primary-button:hover .primary-glow {
  opacity: 1;
}

@keyframes gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.primary-button:active {
  transform: translateY(0) scale(1.02);
}

/* Clear Button (Red variant) */
.clear-button {
  position: relative;
  color: white;
  font-weight: 700;
  border: none;
}

.clear-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #dc2626, #b91c1c, #991b1b);
  border-radius: 12px;
  transition: all 0.3s;
}

.clear-glow {
  position: absolute;
  inset: -4px;
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.6), rgba(185, 28, 28, 0.4));
  border-radius: 16px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
  filter: blur(12px);
}

.clear-button:hover {
  transform: translateY(-2px) scale(1.05);
}

.clear-button:hover .clear-gradient {
  background: linear-gradient(135deg, #b91c1c, #991b1b, #7f1d1d);
  animation: gradient-shift 2s ease-in-out infinite;
}

.clear-button:hover .clear-glow {
  opacity: 1;
}

.clear-button:active {
  transform: translateY(0) scale(1.02);
}

/* Button Elements */
.button-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.button-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.primary-button:hover .button-icon {
  transform: scale(1.1);
}

.clear-button:hover .button-icon {
  transform: scale(1.1);
}

/* Responsive */
@media (max-width: 640px) {
  .modern-button {
    min-width: 100%;
    height: 52px;
    font-size: 16px;
  }
  
  .flex.gap-3 {
    flex-direction: column;
  }
}

/* Dark Mode */
@media (prefers-color-scheme: dark) {
  .primary-gradient {
    background: linear-gradient(135deg, rgb(var(--teal-9)), rgb(var(--teal-10)), rgb(var(--teal-11)));
  }
  
  .primary-button:hover .primary-gradient {
    background: linear-gradient(135deg, rgb(var(--teal-10)), rgb(var(--teal-11)), rgb(var(--teal-12)));
  }
  
  .clear-gradient {
    background: linear-gradient(135deg, #dc2626, #b91c1c, #991b1b);
  }
  
  .clear-button:hover .clear-gradient {
    background: linear-gradient(135deg, #b91c1c, #991b1b, #7f1d1d);
  }

}</style>

<style scoped>
/* ... seus estilos existentes ... */

/* Forçar largura máxima nos componentes internos do Dialog */
:deep(.grid.gap-6) {
  width: 100%;
  max-width: 100%;
}

:deep(.grid.gap-4) {
  width: 100%;
  max-width: 100%;
}

:deep(.filter-group) {
  width: 100%;
  max-width: 100%;
}

/* Forçar inputs e selects a ocuparem mais espaço */
:deep(.flex.items-center.gap-2) {
  width: 100%;
  max-width: 100%;
  flex-wrap: nowrap;
}

:deep(.flex.items-start.gap-2) {
  width: 100%;
  max-width: 100%;
}

/* Aumentar largura dos inputs de valores */
:deep(input[type="text"]),
:deep(input[type="number"]) {
  min-width: 300px !important;
  flex: 1;
}

/* Aumentar largura dos dropdowns */
:deep(.relative.inline-block) {
  min-width: 150px;
}

/* Container do filtro individual */
:deep(.flex.items-center.gap-2.rounded-md) {
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
}

/* Garantir que o último elemento (botão delete) não encolha */
:deep(.flex.items-center.gap-2.rounded-md > *:last-child) {
  flex-shrink: 0;
}

/* Fazer o input de valor crescer para preencher espaço */
:deep(.flex.items-center.gap-2.rounded-md > div.relative.flex) {
  flex: 1;
  min-width: 0;
}
</style>